import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  FlaskConical, UserCog, Ghost, Database, Play, Copy, Check, Trash2,
  RefreshCw, AlertTriangle, ExternalLink, ArrowRight, Circle,
  CheckCircle2, XCircle, ChevronDown, ChevronRight, Filter,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import PageHeader from '@/components/PageHeader'
import { testEnvAPI, lanEditionsAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { FLOW_DOMAINS, allFlows } from './testFlows.data'

/* Testhub — één plek voor alles rond testen.

   Vijf blokken:
     1. Klaarzetten (test-accounts, test-LAN)
     2. Test-accounts + ghost-login
     3. Voortgang-dashboard (progress per flow)
     4. Test-flows detail (per stap checkbaar met status)
     5. Opruimen na testronde

   Elke flow-stap heeft een stabiele id en persistente status
   (pending / ok / problem) + optionele notitie. Runs blijven bewaard
   zodat je tussen sessies verder kan werken. */

const STATUS_META = {
  pending: { icon: Circle,        label: '—',   color: '#94A3B8', chip: 'bg-muted/30 text-muted-foreground' },
  ok:      { icon: CheckCircle2,  label: 'ok',  color: '#61E294', chip: 'bg-live/15 text-live' },
  problem: { icon: XCircle,       label: 'bug', color: '#FF5A5A', chip: 'bg-signal/15 text-signal' },
}

const COLOR_TOKEN = {
  acid:  '#B4FF39',
  gold:  '#E4C265',
  peach: '#FFB57A',
  live:  '#FF3399',
  slate: '#94A3B8',
}

export default function TestflowsPage() {
  const { user } = useAuth()
  const [status, setStatus]     = useState(null)
  const [runs, setRuns]         = useState([])           // laatste run per flow
  const [bezig, setBezig]       = useState(false)
  const [fout, setFout]         = useState(null)
  const [openFlow, setOpenFlow] = useState(null)         // flowId van open detail
  const [filterDom, setFilterDom] = useState('all')

  const isSuper = user?.role === 'superadmin'

  const laadStatus = useCallback(() => {
    testEnvAPI.status()
      .then(r => setStatus(r.data))
      .catch(e => setFout(e.response?.data?.error ?? e.message))
  }, [])
  const laadFlows = useCallback(() => {
    testEnvAPI.listFlows()
      .then(r => setRuns(r.data?.runs || []))
      .catch(() => {})
  }, [])
  useEffect(() => { laadStatus(); laadFlows() }, [laadStatus, laadFlows])

  const doen = async (fn) => {
    setBezig(true); setFout(null)
    try { await fn(); await laadStatus() }
    catch (e) { setFout(e.response?.data?.error ?? e.message) }
    finally { setBezig(false) }
  }

  const testData = status?.is_test_lan
  const heeftUsers = (status?.test_users?.length ?? 0) >= (status?.test_users_definitie?.length ?? 0)

  const runByFlow = useMemo(() => {
    const m = {}
    for (const r of runs) m[r.flow_id] = r
    return m
  }, [runs])

  // Overall totals over alle flows
  const totals = useMemo(() => {
    const flows = allFlows()
    let totalSteps = 0, ok = 0, problem = 0
    for (const f of flows) {
      totalSteps += f.stappen.length
      const run = runByFlow[f.id]
      if (run) {
        ok += Number(run.counts?.ok || 0)
        problem += Number(run.counts?.problem || 0)
      }
    }
    return { totalSteps, ok, problem, pending: totalSteps - ok - problem }
  }, [runByFlow])

  return (
    <div>
      <PageHeader
        icon={FlaskConical}
        title="Testhub"
        subtitle="Elke feature stap-voor-stap testbaar — vink per stap ok of probleem"
      />

      {fout && (
        <Card className="mb-4" style={{ borderLeft: '2px solid #FF5A5A' }}>
          <CardContent className="p-3 flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-signal shrink-0" />
            <span className="text-sm">{fout}</span>
          </CardContent>
        </Card>
      )}

      {/* ─── Voortgang-dashboard ────────────────────────────────────── */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FlaskConical className="h-4 w-4" /> Voortgang
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            <ProgressStat label="Totaal stappen" value={totals.totalSteps} color="#94A3B8" />
            <ProgressStat label="OK"            value={totals.ok}         color="#61E294" />
            <ProgressStat label="Problemen"     value={totals.problem}    color="#FF5A5A" />
            <ProgressStat label="Nog te doen"   value={totals.pending}    color="#7BC8FF" />
          </div>
          <div className="mt-3 h-2 rounded-full bg-muted/30 overflow-hidden flex">
            {totals.totalSteps > 0 && (
              <>
                <div style={{ width: `${(totals.ok/totals.totalSteps)*100}%`, background: '#61E294' }} />
                <div style={{ width: `${(totals.problem/totals.totalSteps)*100}%`, background: '#FF5A5A' }} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── 1. Klaarzetten ─────────────────────────────────────────── */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" /> Klaarzetten
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Stap
              gedaan={heeftUsers}
              titel="Test-accounts"
              tekst={`${status?.test_users?.length ?? 0} van ${status?.test_users_definitie?.length ?? 0} klaar`}
              knop="Aanmaken of resetten"
              onKlik={() => doen(testEnvAPI.seedUsers)}
              bezig={bezig}
            />
            <Stap
              gedaan={testData}
              titel="Test-LAN met data"
              tekst={testData
                ? `${status?.edition?.name} — actief`
                : 'Nog geen test-LAN actief'}
              knop={testData ? 'Nieuwe populate' : 'Test-LAN vullen'}
              onKlik={() => doen(lanEditionsAPI.populateTest)}
              bezig={bezig}
            />
          </div>
          {testData && (
            <div className="pt-3 border-t border-border/40">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Wat er in de test-LAN staat
              </div>
              <div className="grid gap-2 grid-cols-2 md:grid-cols-5 text-sm">
                {[
                  ['Kempenvision', status.data?.kempenvision],
                  ['Image Compo',  status.data?.imagecompo],
                  ['Toernooien',   status.data?.tournaments],
                  ['Programma',    status.data?.programma],
                  ['Schermen',     status.data?.schermen],
                ].map(([label, n]) => (
                  <div key={label} className="rounded-[4px] border border-border bg-card p-2 text-center">
                    <div className="text-xl font-bold tabular-nums leading-none">{n ?? 0}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── 2. Test-accounts ───────────────────────────────────────── */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCog className="h-4 w-4" /> Test-accounts
          </CardTitle>
          {status?.test_wachtwoord && (
            <p className="text-xs text-muted-foreground">
              Alle test-accounts hebben wachtwoord{' '}
              <code className="text-acid">{status.test_wachtwoord}</code>. Niet buiten deze omgeving gebruiken.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {!status?.test_users?.length ? (
            <p className="text-sm text-muted-foreground">
              Nog geen test-accounts. Klik hierboven op "Aanmaken of resetten".
            </p>
          ) : (
            <div className="space-y-1.5">
              {status.test_users.map(u => (
                <TestUser key={u.id} user={u} isSuper={isSuper} setFout={setFout} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── 3. Flows — per domein, met filter ──────────────────────── */}
      <Card className="mb-4">
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Play className="h-4 w-4" /> Test-flows
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Werk met twee vensters: één als jezelf (admin), één als test-user via ghost-login.
              Elke stap krijgt een status — vergeet niet op te slaan wat er niet werkt.
            </p>
          </div>
          <select value={filterDom} onChange={e => setFilterDom(e.target.value)}
                  className="text-xs bg-card border border-border rounded px-2 py-1">
            <option value="all">Alle domeinen</option>
            {FLOW_DOMAINS.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
        </CardHeader>
        <CardContent className="space-y-4">
          {FLOW_DOMAINS
            .filter(d => filterDom === 'all' || d.id === filterDom)
            .map(domain => (
              <DomainBlock key={domain.id} domain={domain}
                           runByFlow={runByFlow}
                           openFlow={openFlow}
                           setOpenFlow={setOpenFlow}
                           onRefresh={laadFlows}
                           setFout={setFout}
                           isSuper={isSuper} />
            ))}
        </CardContent>
      </Card>

      {/* ─── 4. Opruimen ────────────────────────────────────────────── */}
      <Card style={{ borderLeft: '2px solid #FFB57A' }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-peach" /> Na de testronde opruimen
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Alleen test-accounts en test-LAN. Je echte data blijft ongemoeid. Run-historiek blijft.
          </p>
        </CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => {
            if (window.confirm('Alle test.* accounts verwijderen?')) doen(testEnvAPI.wisUsers)
          }} disabled={bezig}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Test-accounts wissen
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { laadStatus(); laadFlows() }}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Verversen
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

/* ── Bouwstenen ──────────────────────────────────────────────────── */

function ProgressStat({ label, value, color }) {
  return (
    <div className="rounded-[4px] border border-border bg-card p-3 text-center">
      <div className="text-2xl font-bold tabular-nums leading-none" style={{ color }}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1.5">{label}</div>
    </div>
  )
}

function Stap({ gedaan, titel, tekst, knop, onKlik, bezig }) {
  return (
    <div className="rounded-[4px] border border-border bg-card p-3 flex items-start gap-3"
         style={{ borderLeft: `2px solid ${gedaan ? '#61E294' : '#94A3B8'}` }}>
      <div className={`h-6 w-6 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
        gedaan ? 'border-live text-live' : 'border-border text-muted-foreground'
      }`}>
        {gedaan ? <Check className="h-3.5 w-3.5" /> : <span className="text-xs">?</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{titel}</div>
        <div className="text-xs text-muted-foreground">{tekst}</div>
        <Button size="sm" variant="outline" className="mt-2" onClick={onKlik} disabled={bezig}>
          {knop}
        </Button>
      </div>
    </div>
  )
}

function TestUser({ user, isSuper, setFout }) {
  const [gekopieerd, setGek] = useState(false)
  const [ghost, setGhost]    = useState(null)

  const kopieer = (tekst) => {
    navigator.clipboard?.writeText(tekst)
    setGek(true); setTimeout(() => setGek(false), 1500)
  }

  const startGhost = async () => {
    if (user.role === 'superadmin') { setFout('Geen ghost op superadmin'); return }
    try {
      const { data } = await testEnvAPI.maakGhost(user.id, 10)
      const url = new URL(data.inlog_url, window.location.origin).href
      setGhost(url)
      navigator.clipboard?.writeText(url).catch(() => {})
      window.open(url, '_blank', 'noopener')
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
  }

  const rolKleur = {
    superadmin: '#FF5A5A', admin: '#E4C265',
    referee:    '#7BC8FF', helpdesk: '#C77BFF',
    user:       '#94A3B8',
  }[user.role] ?? '#94A3B8'

  return (
    <div className="flex items-center gap-3 rounded-[4px] border border-border bg-card px-3 py-2 flex-wrap"
         style={{ borderLeft: `2px solid ${rolKleur}` }}>
      <span className="font-mono text-[10px] uppercase tracking-widest w-16 shrink-0"
            style={{ color: rolKleur }}>{user.role}</span>
      <span className="font-semibold text-sm min-w-[8rem]">{user.display_name || user.username}</span>
      <code className="text-xs text-muted-foreground">{user.username}</code>

      <div className="ml-auto flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => kopieer(user.username)} title="Gebruikersnaam kopiëren">
          {gekopieerd ? <Check className="h-4 w-4 text-live" /> : <Copy className="h-4 w-4" />}
        </Button>
        {isSuper && user.role !== 'superadmin' && (
          <Button size="sm" variant="outline" onClick={startGhost}
                  title="Eenmalige inlog-URL — opent in nieuw tabblad">
            <Ghost className="h-4 w-4 mr-1.5" /> Ghost
          </Button>
        )}
      </div>

      {ghost && (
        <div className="w-full mt-1 pt-2 border-t border-border/40 flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Eenmalige URL (10 min, ook op klembord):</span>
          <code className="text-acid truncate flex-1">{ghost}</code>
        </div>
      )}
    </div>
  )
}

/* Domein-blok met vlaggen per flow + expandable detail */
function DomainBlock({ domain, runByFlow, openFlow, setOpenFlow, onRefresh, setFout, isSuper }) {
  const color = COLOR_TOKEN[domain.color] || COLOR_TOKEN.acid
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-1.5 w-6 rounded-full" style={{ background: color }} />
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{domain.label}</div>
      </div>
      <div className="space-y-1.5">
        {domain.flows.map(flow => (
          <FlowRow key={flow.id} flow={flow} color={color}
                   run={runByFlow[flow.id]}
                   open={openFlow === flow.id}
                   onToggle={() => setOpenFlow(openFlow === flow.id ? null : flow.id)}
                   onRefresh={onRefresh}
                   setFout={setFout}
                   isSuper={isSuper} />
        ))}
      </div>
    </div>
  )
}

function FlowRow({ flow, color, run, open, onToggle, onRefresh, setFout, isSuper }) {
  const totaal = flow.stappen.length
  const okN = Number(run?.counts?.ok || 0)
  const probN = Number(run?.counts?.problem || 0)
  const pct = totaal ? Math.round(((okN + probN) / totaal) * 100) : 0

  return (
    <div className="rounded-[4px] border border-border bg-card overflow-hidden">
      <button onClick={onToggle}
              className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/[.03]">
        {open
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
        <span className="flex-1 font-semibold text-sm">{flow.titel}</span>
        {flow.rol && (
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            als {flow.rol}
          </span>
        )}
        {/* Compact progress */}
        <div className="flex items-center gap-2 shrink-0">
          {okN > 0 && (
            <span className="flex items-center gap-1 text-xs text-live">
              <CheckCircle2 className="h-3 w-3" /> {okN}
            </span>
          )}
          {probN > 0 && (
            <span className="flex items-center gap-1 text-xs text-signal">
              <XCircle className="h-3 w-3" /> {probN}
            </span>
          )}
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums w-14 text-right">
            {okN + probN}/{totaal}
          </span>
        </div>
      </button>
      {/* Progress-bar */}
      {(okN + probN) > 0 && (
        <div className="h-[3px] bg-muted/30 flex">
          <div style={{ width: `${(okN/totaal)*100}%`, background: '#61E294' }} />
          <div style={{ width: `${(probN/totaal)*100}%`, background: '#FF5A5A' }} />
        </div>
      )}
      {open && (
        <FlowDetail flow={flow} run={run} onRefresh={onRefresh}
                    setFout={setFout} isSuper={isSuper} color={color} />
      )}
    </div>
  )
}

function FlowDetail({ flow, run, onRefresh, setFout, isSuper, color }) {
  const [current, setCurrent] = useState(run || null)
  const [steps, setSteps]     = useState({})   // step_id → {status, note}
  const [busy, setBusy]       = useState(false)

  // Bij open: als er nog geen run is, starten we er meteen een.
  useEffect(() => {
    let cancel = false
    ;(async () => {
      try {
        setBusy(true)
        const r = await testEnvAPI.startRun(flow.id)
        if (cancel) return
        setCurrent(r.data?.run)
        const map = {}
        for (const s of (r.data?.steps || [])) map[s.step_id] = s
        setSteps(map)
      } catch (e) {
        setFout(e.response?.data?.error ?? e.message)
      } finally { setBusy(false) }
    })()
    return () => { cancel = true }
  }, [flow.id, setFout])

  const setStatus = async (stepId, status, note) => {
    if (!current) return
    const prev = steps[stepId] || {}
    // Optimistische update
    setSteps(m => ({ ...m, [stepId]: { ...prev, status, note: note ?? prev.note ?? null } }))
    try {
      await testEnvAPI.setStep(current.id, stepId, {
        status,
        note: note != null ? note : (prev.note ?? null),
      })
      onRefresh()
    } catch (e) {
      // rollback
      setSteps(m => ({ ...m, [stepId]: prev }))
      setFout(e.response?.data?.error ?? e.message)
    }
  }

  const reset = async () => {
    if (!current) return
    if (!window.confirm('Alle stappen van deze flow op pending zetten?')) return
    try {
      await testEnvAPI.resetRun(current.id)
      setSteps({})
      onRefresh()
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
  }

  const startGhostFor = async (username) => {
    try {
      const usersR = await testEnvAPI.status()
      const target = (usersR.data?.test_users || []).find(u => u.username === username)
      if (!target) { setFout(`Test-user ${username} niet gevonden — seed test-accounts eerst.`); return }
      const { data } = await testEnvAPI.maakGhost(target.id, 10)
      const url = new URL(data.inlog_url, window.location.origin).href
      navigator.clipboard?.writeText(url).catch(() => {})
      window.open(url, '_blank', 'noopener')
    } catch (e) { setFout(e.response?.data?.error ?? e.message) }
  }

  return (
    <div className="border-t border-border/40 p-3 space-y-2">
      {flow.doel && (
        <p className="text-xs text-muted-foreground leading-relaxed mb-2">{flow.doel}</p>
      )}
      <div className="flex gap-2 flex-wrap">
        {flow.start && (
          <Button asChild size="sm" variant="outline">
            <Link to={flow.start} target="_blank">
              Open start-pagina <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </Link>
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={reset} disabled={busy || !current}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reset flow
        </Button>
      </div>
      <div className="space-y-1">
        {flow.stappen.map((s, i) => (
          <StepRow key={s.id} idx={i + 1} step={s}
                   state={steps[s.id]}
                   onStatus={setStatus}
                   onGhost={s.ghost && isSuper ? () => startGhostFor(s.ghost) : null}
                   accentColor={color} />
        ))}
      </div>
    </div>
  )
}

function StepRow({ idx, step, state, onStatus, onGhost, accentColor }) {
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState(state?.note || '')
  useEffect(() => { setNote(state?.note || '') }, [state?.note])

  const status = state?.status || 'pending'
  const meta = STATUS_META[status]
  const StatusIcon = meta.icon

  const setStat = (next) => onStatus(step.id, next, note || null)

  const rowBg = status === 'ok'      ? 'bg-live/[.04]'
              : status === 'problem' ? 'bg-signal/[.05]'
              : 'bg-transparent'

  return (
    <div className={`rounded-[4px] border border-border/60 p-2.5 ${rowBg} space-y-1.5`}>
      <div className="flex items-start gap-2.5">
        <span className="font-mono text-[10px] text-muted-foreground w-6 shrink-0 mt-1 text-right">
          {String(idx).padStart(2,'0')}
        </span>
        <StatusIcon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: meta.color }} />
        <span className="flex-1 text-sm leading-snug">{step.tekst}</span>
        <div className="flex gap-1 shrink-0">
          {onGhost && (
            <button onClick={onGhost}
                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-border hover:border-acid hover:text-acid"
                    title={`Ghost naar ${step.ghost}`}>
              <Ghost className="h-3 w-3 inline mr-1" /> {step.ghost}
            </button>
          )}
          <StatusButton current={status} value="ok"      onClick={() => setStat('ok')} />
          <StatusButton current={status} value="problem" onClick={() => setStat('problem')} />
          {status !== 'pending' && (
            <button onClick={() => setStat('pending')}
                    className="text-[10px] font-mono uppercase text-muted-foreground px-1.5 py-1 hover:text-foreground"
                    title="Terug op pending">
              ↺
            </button>
          )}
        </div>
      </div>
      {(status === 'problem' || note || noteOpen) && (
        <div className="pl-11">
          <textarea
            rows={2}
            value={note}
            placeholder={status === 'problem' ? 'Wat ging er mis? (beschrijving, foutmelding, stappen)' : 'Notitie (optioneel)'}
            className="w-full text-xs bg-card/60 border border-border rounded px-2 py-1.5 resize-y"
            onChange={e => setNote(e.target.value)}
            onBlur={() => onStatus(step.id, status, note || null)}
          />
        </div>
      )}
      {!noteOpen && status !== 'problem' && !note && (
        <div className="pl-11">
          <button onClick={() => setNoteOpen(true)}
                  className="text-[10px] text-muted-foreground hover:text-foreground">
            + notitie toevoegen
          </button>
        </div>
      )}
    </div>
  )
}

function StatusButton({ current, value, onClick }) {
  const meta = STATUS_META[value]
  const active = current === value
  const Icon = meta.icon
  return (
    <button onClick={onClick}
            className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${
              active
                ? value === 'ok'
                  ? 'bg-live/20 border-live text-live'
                  : 'bg-signal/20 border-signal text-signal'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
            title={`Markeer als ${meta.label}`}>
      <Icon className="h-3 w-3" />
      <span className="hidden sm:inline">{meta.label}</span>
    </button>
  )
}
